import { useEffect, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import './CustomAlert.css';

type AlertOptions = {
  message: string;
  type?: 'info' | 'error' | 'success' | 'confirm';
  onConfirm?: () => void;
  onCancel?: () => void;
};

let openAlert: ((options: AlertOptions) => void) | null = null;

export const openCustomAlert = (options: AlertOptions) => {
  if (openAlert) openAlert(options);
};

export const AlertProvider = ({ children }: { children: React.ReactNode }) => {
  const [alertState, setAlertState] = useState({
    visible: false,
    message: '',
    type: 'info' as AlertOptions['type'],
    onConfirm: undefined as (() => void) | undefined,
    onCancel: undefined as (() => void) | undefined,
  });

  useEffect(() => {
    openAlert = (options) => {
      setAlertState({
        visible: true,
        message: options.message,
        type: options.type || 'info',
        onConfirm: options.onConfirm,
        onCancel: options.onCancel,
      });
    };
    return () => {
      openAlert = null;
    };
  }, []);

  const close = useCallback(() => {
    setAlertState((prev) => ({ ...prev, visible: false }));
  }, []);

  const handleConfirm = useCallback(() => {
    if (alertState.onConfirm) alertState.onConfirm();
    close();
  }, [alertState, close]);

  const handleCancel = useCallback(() => {
    if (alertState.onCancel) alertState.onCancel();
    close();
  }, [alertState, close]);

  if (!alertState.visible) {
    return <>{children}</>;
  }

  return (
    <>
      {children}
      {createPortal(
        <div className="ca-overlay" onClick={handleCancel}>
          <div
            className={`ca-dialog ${alertState.type}`}
            role="alertdialog"
            aria-modal="true"
            aria-describedby="custom-alert-message"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ca-body">
              <p id="custom-alert-message" className="ca-message">{alertState.message}</p>
            </div>
            <div className="ca-footer">
              {alertState.type === 'confirm' && (
                <button type="button" className="ca-cancel" onClick={handleCancel}>
                  취소
                </button>
              )}
              <button type="button" className="ca-confirm" onClick={handleConfirm}>
                {alertState.type === 'confirm' ? '삭제' : '확인'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};
